// Система базового аудит-логирования для HR админ-панели
// Хранит логи в localStorage (в продакшене - в БД)

import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getSession } from './auth';

const AUDIT_LOG_KEY = "hr_admin_audit_log";
const MAX_LOG_ENTRIES = 1000; // Максимальное количество записей в логе

/**
 * Типы действий для логирования
 */
export const ActionTypes = {
  // Должности
  POSITION_CREATED: 'POSITION_CREATED',
  POSITION_UPDATED: 'POSITION_UPDATED', 
  POSITION_DELETED: 'POSITION_DELETED',
  POSITION_MOVED: 'POSITION_MOVED',
  
  // Отделы
  DEPARTMENT_CREATED: 'DEPARTMENT_CREATED',
  DEPARTMENT_UPDATED: 'DEPARTMENT_UPDATED',
  DEPARTMENT_DELETED: 'DEPARTMENT_DELETED',
  
  // Карьерные переходы
  TRANSITION_CREATED: 'TRANSITION_CREATED',
  TRANSITION_UPDATED: 'TRANSITION_UPDATED',
  TRANSITION_DELETED: 'TRANSITION_DELETED',
  
  // Импорт/экспорт
  DATA_IMPORTED: 'DATA_IMPORTED',
  DATA_EXPORTED: 'DATA_EXPORTED',
  
  // Системные
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  SESSION_EXTENDED: 'SESSION_EXTENDED',
  
  // База данных (новые типы)
  DATABASE_INIT: 'DATABASE_INIT',
  MIGRATION: 'MIGRATION',
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  EXPORT: 'EXPORT',
  BACKUP_CREATED: 'BACKUP_CREATED',
  BULK_UPDATE: 'BULK_UPDATE',
  SYSTEM_CLEANUP: 'SYSTEM_CLEANUP'
};

/**
 * Получение всех логов
 */
export const getAuditLogs = () => {
  try {
    const logsData = localStorage.getItem(AUDIT_LOG_KEY);
    return logsData ? JSON.parse(logsData) : [];
  } catch (error) {
    console.error('Ошибка получения логов:', error);
    return [];
  }
};

/**
 * Добавление записи в аудит-лог
 */
export const logAction = (actionType, details = {}) => {
  try {
    const session = getSession();
    const user = session ? session.user : 'Unknown';
    
    // Генерируем уникальный ID с случайной составляющей для избежания дублирования
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 5);
    const uniqueId = `${timestamp}_${randomSuffix}`;
    
    const logEntry = {
      id: uniqueId,
      timestamp: timestamp,
      formattedTime: format(new Date(), 'dd.MM.yyyy HH:mm:ss', { locale: ru }),
      user,
      action: actionType,
      details,
      userAgent: navigator.userAgent
    };
    
    const logs = getAuditLogs();
    logs.unshift(logEntry); // Добавляем в начало массива
    
    // Ограничиваем количество записей
    if (logs.length > MAX_LOG_ENTRIES) {
      logs.splice(MAX_LOG_ENTRIES);
    }
    
    localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(logs));
    
    console.log(`[AUDIT] ${actionType}:`, details);
    return logEntry;
  } catch (error) {
    console.error('Ошибка логирования:', error);
  }
};

/**
 * Получение читаемого описания действия
 */
export const getActionDescription = (actionType, details) => {
  const descriptions = {
    [ActionTypes.POSITION_CREATED]: `Создана должность "${details.title}" в отделе "${details.department}"`,
    [ActionTypes.POSITION_UPDATED]: `Обновлена должность "${details.title}"`,
    [ActionTypes.POSITION_DELETED]: `Удалена должность "${details.title}"`,
    [ActionTypes.POSITION_MOVED]: `Должность "${details.title}" перемещена из "${details.fromDepartment}" в "${details.toDepartment}"`,
    
    [ActionTypes.DEPARTMENT_CREATED]: `Создан отдел "${details.name}"`,
    [ActionTypes.DEPARTMENT_UPDATED]: `Обновлен отдел "${details.name}"`,
    [ActionTypes.DEPARTMENT_DELETED]: `Удален отдел "${details.name}"`,
    
    [ActionTypes.TRANSITION_CREATED]: `Создан переход "${details.from}" → "${details.to}"`,
    [ActionTypes.TRANSITION_UPDATED]: `Обновлен переход "${details.from}" → "${details.to}"`,
    [ActionTypes.TRANSITION_DELETED]: `Удален переход "${details.from}" → "${details.to}"`,
    
    [ActionTypes.DATA_IMPORTED]: `Импортированы данные из ${details.source}`,
    [ActionTypes.DATA_EXPORTED]: `Экспортированы данные в формате ${details.format}`,
    
    [ActionTypes.LOGIN]: 'Вход в админ-панель',
    [ActionTypes.LOGOUT]: 'Выход из админ-панели',
    [ActionTypes.SESSION_EXTENDED]: 'Продлена сессия',
    
    // Новые описания для БД операций
    [ActionTypes.DATABASE_INIT]: 'Инициализирована база данных SQLite',
    [ActionTypes.MIGRATION]: `Миграция данных: ${details.departments || 0} отделов, ${details.positions || 0} должностей, ${details.transitions || 0} переходов`,
    [ActionTypes.CREATE]: `Создана запись в таблице ${details.table}: ${details.id}`,
    [ActionTypes.UPDATE]: `Обновлена запись в таблице ${details.table}: ${details.id}`,
    [ActionTypes.DELETE]: `Удалена запись из таблицы ${details.table}: ${details.id}`,
    [ActionTypes.EXPORT]: `Экспорт данных: ${details.recordsCount || 0} записей`,
    [ActionTypes.BACKUP_CREATED]: `Создан backup базы данных`,
    [ActionTypes.BULK_UPDATE]: `Массовое обновление: ${details.count || 0} записей в таблице ${details.table}`,
    [ActionTypes.SYSTEM_CLEANUP]: `Очистка системы: удалено ${details.removedLogs || 0} старых логов`
  };
  
  return descriptions[actionType] || `Действие: ${actionType}`;
};

/**
 * Фильтрация логов по типу действия
 */
export const filterLogsByAction = (actionType) => {
  const logs = getAuditLogs();
  return logs.filter(log => log.action === actionType);
};

/**
 * Фильтрация логов по дате
 */
export const filterLogsByDate = (startDate, endDate) => {
  const logs = getAuditLogs();
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  
  return logs.filter(log => log.timestamp >= start && log.timestamp <= end);
};

/**
 * Экспорт логов в CSV формат
 */
export const exportLogsToCSV = () => {
  const logs = getAuditLogs();
  
  const csvHeaders = ['Время', 'Пользователь', 'Действие', 'Описание', 'Детали'];
  const csvRows = logs.map(log => [
    log.formattedTime,
    log.user,
    log.action,
    getActionDescription(log.action, log.details),
    JSON.stringify(log.details)
  ]);
  
  const csvContent = [
    csvHeaders.join(','),
    ...csvRows.map(row => row.map(field => `"${field}"`).join(','))
  ].join('\n');
  
  return csvContent;
};

/**
 * Очистка старых логов (старше указанного количества дней)
 */
export const cleanupOldLogs = (daysToKeep = 90) => {
  const logs = getAuditLogs();
  const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
  
  const filteredLogs = logs.filter(log => log.timestamp > cutoffTime);
  localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(filteredLogs));
  
  const removedCount = logs.length - filteredLogs.length;
  if (removedCount > 0) {
    logAction(ActionTypes.SYSTEM_CLEANUP, { removedLogs: removedCount, daysKept: daysToKeep });
  }
  
  return removedCount;
}; 